import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

import { AuthService } from './auth.service';

// Errors
import { AppError } from '@app/core/errors/app-error';
import { NoConnection } from '@app/core/errors/no-connection';
import { BadRequest } from '@app/core/errors/response-errors/bad-request';
import { NotAcceptable } from '@app/core/errors/response-errors/not-acceptable';
import { NotFound } from '@app/core/errors/response-errors/not-found';
import { Unauthorized } from '@app/core/errors/response-errors/unauthorized';
import { UnprocessableEntity } from '@app/core/errors/response-errors/unprocessable-entity';
import { AuthTokens } from '@app/interfaces/auth-tokens';
import { AuthSelectors, FrontendLogout, SaveAuthTokens } from '@app/store';
import { AuthStateModel } from '@app/store/auth/auth-state.model';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Store } from '@ngxs/store';
import { NewAuthTokenResponse } from './interfaces/new-access-token-response';
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isBusy = false;
  private repeatWhenUpdated$ = new Subject<string>();

  constructor(
    private store: Store,
    private authService: AuthService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    const requireToken = this.requireToken(req);

    return this.handle(req, next).pipe(
      catchError((error: any) => {
        return this.refreshTokenFailureHandler(error, requireToken);
      }),
    );
  }

  requireToken(req: HttpRequest<any>): boolean {
    // Extract only the path from the URL
    let path = new URL(req.url).pathname; // remove prefix '/api' from it, if it exists

    // Remove the '/api' prefix if it exists
    path = path.replace(/^\/api/, '');

    const openUrls = [
      '/token',
      '/token/refresh',
      '/auth/google',
      '/auth/password-reset',
      '/auth/password-reset/confirm',
      '/category/list',
      '/category/top',
      '/country/list',
      // '/gallery', // '/gallery' is commented, thus it is required. It is required because of is_favorite flag
      '/search/latest',
      '/search/popular',
      '/tag/popular',
      '/tag/popular/extended',
    ];

    // Check if the request URL exactly matches any in openUrls
    if (openUrls.includes(path)) {
      return false;
    }

    // Both commented, out, thus they are required. They are required because of is_favorite flag
    // const regexPatterns = [
    //   /^\/image\/[1-9]\d*$/, // Matches "/image/123" (numbers only)
    //   /^\/image\/[1-9]\d*\/related$/, // Matches "/image/123/related"
    // ];

    // // Check if the request URL matches any of the regex patterns
    // if (regexPatterns.some((pattern) => pattern.test(path))) {
    //   return false;
    // }

    // If no match was found, require a token
    return true;
  }

  private handle(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (!this.requireToken(req)) {
      //how can I delete authorization header here?
      const newReq = req.clone({
        headers: req.headers.delete('Authorization'),
      });
      return next.handle(newReq);
    }
    return this.store.selectOnce(AuthSelectors.authTokens).pipe(
      switchMap((authTokens: AuthTokens) => {
        if (authTokens.token) {
          const jwtHelper = new JwtHelperService();
          if (!jwtHelper.isTokenExpired(authTokens.token, 30)) {
            const newReq = req.clone({
              setHeaders: {
                authorization: 'Bearer ' + authTokens.token,
              },
            });
            return next.handle(newReq);
          }

          if (this.isBusy) {
            return this.repeatWhenUpdated$.pipe(
              take(1),
              switchMap((token: string) => {
                const newReq = req.clone({
                  setHeaders: {
                    authorization: 'Bearer ' + token,
                  },
                });
                return next.handle(newReq);
              }),
            );
          }

          this.isBusy = true;
          return this.authService
            .renewAccessToken(authTokens.refreshToken as string)
            .pipe(
              switchMap((response: NewAuthTokenResponse) => {
                this.store.dispatch(new SaveAuthTokens(response));
                const newReq = req.clone({
                  setHeaders: {
                    authorization: 'Bearer ' + response.access,
                  },
                });
                this.isBusy = false;
                this.repeatWhenUpdated$.next(response.access);
                return next.handle(newReq);
              }),
              catchError((refreshTokenError: any) => {
                this.isBusy = false;
                return this.refreshTokenFailureHandler(refreshTokenError);
              }),
            );
        }

        return next.handle(req);
      }),
      catchError((error: any) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 401) {
            // JWT expired, go to login
            if (
              error.error.message === 'Expired JWT Token' ||
              error.error.message === 'Invalid JWT Token' ||
              error.error.message ===
                'Your request was made with invalid credentials.'
            ) {
              if (this.isBusy) {
                return this.repeatWhenUpdated$.pipe(
                  take(1),
                  switchMap((token: string) => {
                    const newReq = req.clone({
                      setHeaders: {
                        authorization: 'Bearer ' + token,
                      },
                    });
                    return next.handle(newReq);
                  }),
                );
              }

              this.isBusy = true;
              return this.store
                .selectOnce((state: any) => {
                  return state.auth;
                })
                .pipe(
                  switchMap((state: AuthStateModel) => {
                    if (!state.refreshToken) {
                      throwError(() => {
                        return 'Refresh token is missing';
                      });
                    }
                    return this.authService
                      .renewAccessToken(state.refreshToken as string)
                      .pipe(
                        switchMap((response: NewAuthTokenResponse) => {
                          this.store.dispatch(new SaveAuthTokens(response));
                          const newReq = req.clone({
                            setHeaders: {
                              authorization: 'Bearer ' + response.access,
                            },
                          });

                          this.isBusy = false;
                          this.repeatWhenUpdated$.next(response.access);
                          return next.handle(newReq);
                        }),
                      );
                  }),
                  catchError((refreshTokenError: any) => {
                    this.isBusy = false;
                    return this.refreshTokenFailureHandler(refreshTokenError);
                  }),
                );
            }
          }
        }

        return throwError(() => error);
      }),
    );
  }

  private refreshTokenFailureHandler(
    err: any,
    logoutOnError: boolean = true,
  ): Observable<any> {
    return this.convertErrorToAppError(err).pipe(
      catchError((error: any) => {
        if (logoutOnError && error instanceof Unauthorized) {
          this.store.dispatch(new FrontendLogout());
        }

        return throwError(() => error);
      }),
    );
  }

  private convertErrorToAppError(error: any) {
    if (error instanceof AppError) {
      return throwError(() => error);
    }

    if (error instanceof HttpErrorResponse) {
      if (error.status === 401) {
        return throwError(() => new Unauthorized(error));
      }

      if (error.status === 400) {
        return throwError(() => new BadRequest(error));
      }

      if (error.status === 404) {
        return throwError(() => new NotFound(error));
      }

      if (error.status === 406) {
        return throwError(() => new NotAcceptable(error));
      }

      if (error.status === 422) {
        if (!(error as any).url.endsWith('/token')) {
          // this.store.dispatch(
          //   new ErrorMessage(JSON.stringify(error.error.errors))
          // );
        }
        return throwError(() => new UnprocessableEntity(error));
      }

      if (error.status === 0) {
        console.error('Connection error!');
        // alert('Your internet connection is lost! Please, reload the website!');
        // location.reload();
        return throwError(() => new NoConnection());
      }
    }

    return throwError(() => new AppError(error));
  }
}
